import { Truncate } from "@components/Truncate";
import find from "lodash/find";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import {
  Container,
  Icon,
  Label,
  List,
  Message,
  Popup,
} from "semantic-ui-react";
import { HitsSearch } from "./HitsSearch";

class ESSelector extends Component {
  constructor(props) {
    super(props);

    const selections = [];
    for (const selection of props.initialSelections) {
      if (props.onSelectResult) {
        props.onSelectResult(selection, true);
      }
      selections.push(selection);
    }

    this.searchRef = null;
    this.state = {
      selections: selections,
    };
  }

  updateSelections = (selections) => {
    const { onSelectionsUpdate } = this.props;
    this.setState({ selections });
    onSelectionsUpdate(selections);
  };

  addSingleSelection = (selection) => {
    const selections = [selection];
    this.updateSelections(selections);
  };

  addMultiSelection = (selection) => {
    const { selections } = this.state;
    const currentSelections = [...selections];
    let hasMatch = find(currentSelections, (sel) => sel.id === selection.id);

    if (!hasMatch) {
      currentSelections.push(selection);
      this.updateSelections(currentSelections);
    }
  };

  onSelectResult = (result) => {
    const { onSelectResult, multiple } = this.props;
    if (onSelectResult) {
      onSelectResult(result);
    }
    return multiple
      ? this.addMultiSelection(result)
      : this.addSingleSelection(result);
  };

  removeSelection = (selection) => {
    const { selections } = this.state;
    const { onRemoveSelection } = this.props;
    // Remove from state
    const newSelections = selections.filter(
      (currentSelection) => currentSelection.id !== selection.id
    );
    this.updateSelections(newSelections);
    if (onRemoveSelection) {
      onRemoveSelection(selection);
    }
    // focus back on the input field
    if (this.searchRef) {
      this.searchRef.searchInputRef.focus();
    }
  };

  renderSelection = (selection, removeSelection) => {
    const { renderSelection, icon, disabledMessage } = this.props;
    if (renderSelection) return renderSelection;

    const labelContent = !_isEmpty(disabledMessage) ? (
      <Message color="yellow" size="small" style={{ borderRadius: "8px" }}>
        {disabledMessage}
      </Message>
    ) : (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          backgroundColor: "green",
          borderRadius: "12px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)")
        }
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {icon && <span>{icon}</span>}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Truncate style={{ fontWeight: 600 }}>{selection.title}</Truncate>
            <span
              style={{ fontSize: "0.85em", color: "#555", overflow: "hidden" }}
            >
              {selection.description}
            </span>
          </div>
        </div>
        <Icon
          name="trash alternate outline"
          style={{ marginLeft: "12px", color: "#ff5a5f" }}
          onClick={() => removeSelection(selection)}
        />
      </div>
    );

    return (
      <List.Item key={selection.id} style={{ marginBottom: "8px" }}>
        <List.Content>
          {selection.extra && _isEmpty(disabledMessage) ? (
            <Popup content={selection.extra} trigger={labelContent} />
          ) : (
            labelContent
          )}
        </List.Content>
      </List.Item>
    );
  };

  renderSelections = (selections, renderSelection, removeSelection) => {
    const { renderSelections } = this.props;
    if (renderSelections) return renderSelections;

    return (
      <Container className="result-selections">
        <List>
          {selections.map((selection) =>
            renderSelection(selection, removeSelection)
          )}
        </List>
      </Container>
    );
  };

  renderSelectionInfoText = () => {
    const { selectionInfoText, emptySelectionInfoText } = this.props;
    const { selections } = this.state;
    if (
      !_isEmpty(selections) &&
      (selectionInfoText || emptySelectionInfoText)
    ) {
      return (
        <p>
          {selections.length > 0 ? selectionInfoText : emptySelectionInfoText}
        </p>
      );
    }
  };

  render() {
    const {
      placeholder,
      disabled,
      id,
      name,
      query,
      delay,
      alwaysWildcard,
      minCharacters,
      focus,
      error,
      serializer,
      onSearchChange,
      onResults,
      handleKeyPress,
    } = this.props;
    const { selections } = this.state;
    return (
      <div id="es-selector">
        <HitsSearch
          disabled={disabled}
          id={id}
          name={name}
          query={query}
          delay={delay}
          alwaysWildcard={alwaysWildcard}
          minCharacters={minCharacters}
          placeholder={placeholder}
          focus={focus}
          serializer={serializer}
          onResults={onResults}
          handleKeyPress={handleKeyPress}
          onSelect={this.onSelectResult}
          onSearchChange={onSearchChange}
          ref={(element) => (this.searchRef = element)}
        />
        {error && (
          <Label prompt pointing={error.pointing}>
            {error.content}
          </Label>
        )}
        {this.renderSelectionInfoText()}
        {this.renderSelections(
          selections,
          this.renderSelection,
          this.removeSelection
        )}
      </div>
    );
  }
}

ESSelector.propTypes = {
  alwaysWildcard: PropTypes.bool,
  delay: PropTypes.number,
  disabled: PropTypes.bool,
  initialSelections: PropTypes.array,
  minCharacters: PropTypes.number,
  multiple: PropTypes.bool,
  placeholder: PropTypes.string,
  query: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func,
  onResults: PropTypes.func,
  handleKeyPress: PropTypes.func,
  onSelectResult: PropTypes.func,
  onSelectionsUpdate: PropTypes.func,
  onRemoveSelection: PropTypes.func,
  renderSelections: PropTypes.func,
  renderSelection: PropTypes.func,
  serializer: PropTypes.func,
  id: PropTypes.string,
  name: PropTypes.string,
  icon: PropTypes.any,
  selectionInfoText: PropTypes.string,
  emptySelectionInfoText: PropTypes.string,
  focus: PropTypes.bool,
  error: PropTypes.object,
  disabledMessage: PropTypes.string,
};

ESSelector.defaultProps = {
  delay: 250,
  initialSelections: [],
  minCharacters: 3,
  icon: null,
  onSelectionsUpdate: () => {},
  emptySelectionInfoText: null,
  selectionInfoText: null,
  onResults: null,
  handleKeyPress: null,
  focus: false,
  error: null,
  disabledMessage: "",
  alwaysWildcard: false,
  disabled: false,
  multiple: false,
  placeholder: "",
  onSearchChange: null,
  onSelectResult: null,
  onRemoveSelection: null,
  renderSelections: null,
  renderSelection: null,
  serializer: null,
  id: "",
  name: "",
};

export default Overridable.component("ESSelector", ESSelector);
